import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, resourceIdFromSlug } from '../../lib/utils';
import Seo from '../../components/Seo';
import ClaimResourceModal from '../../components/ClaimResourceModal';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Phone, Globe, MapPin, Mail, Clock, Users, CheckCircle2, ArrowLeft, Share2, Link as LinkIcon, ShieldCheck, Loader2 } from 'lucide-react';

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    <Icon className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
    <div><p className="text-xs uppercase tracking-wide text-gray-400">{label}</p><div className="text-gray-800">{children}</div></div>
  </div>
);

const ResourceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const id = resourceIdFromSlug(slug);
    setLoading(true); setNotFound(false);
    axios.get(`${API_URL}/resources/${id}`)
      .then((r) => setResource(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }
    catch { toast.error('Could not copy link.'); }
  };
  const share = async () => {
    const data = { title: resource?.name, text: `${resource?.name} — a community resource on I Need A Social Worker`, url: window.location.href };
    if (navigator.share) { try { await navigator.share(data); } catch { /* cancelled */ } }
    else copyLink();
  };

  if (loading) return <div className="min-h-screen bg-[#F7F5EF] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (notFound || !resource) {
    return (
      <div className="min-h-screen bg-[#F7F5EF]">
        <Seo title="Resource not found" noindex path={`/community-resources/resource/${slug}`} />
        <div className="max-w-2xl mx-auto px-4 pt-28 pb-16 text-center">
          <h1 className="font-serif text-2xl font-bold text-primary">Resource not found</h1>
          <p className="text-gray-600 mt-2">This resource may have been removed or is awaiting review.</p>
          <Button className="mt-5 bg-primary text-white" onClick={() => navigate('/community-resources')}>Back to Directory</Button>
        </div>
      </div>
    );
  }

  const loc = [resource.city, resource.state].filter(Boolean).join(', ');
  const desc = resource.description ? resource.description.slice(0, 200) : `${resource.name}${loc ? ` in ${loc}` : ''} — a verified community resource.`;

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <Seo title={`${resource.name}${loc ? ` — ${loc}` : ''}`} description={desc} type="article" />
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <Link to="/community-resources" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-3"><ArrowLeft className="w-4 h-4" /> Community Resource Directory</Link>
          <span className="inline-block text-xs font-semibold text-secondary uppercase tracking-wide">{resource.category}</span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold mt-1" data-testid="resource-detail-name">{resource.name}</h1>
          {resource.is_verified && (
            <p className="inline-flex items-center gap-1.5 mt-3 text-green-300 text-sm font-semibold"><ShieldCheck className="w-4 h-4" /> Verified by INASW</p>
          )}
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl border p-6 shadow-sm">
          <div className="flex flex-wrap gap-2 mb-4">
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="resource-copy-link"><LinkIcon className="w-4 h-4 mr-1" /> Copy Link</Button>
            <Button size="sm" variant="outline" onClick={share} data-testid="resource-share"><Share2 className="w-4 h-4 mr-1" /> Share</Button>
            {resource.website && <a href={resource.website} target="_blank" rel="noreferrer"><Button size="sm" className="bg-primary text-white"><Globe className="w-4 h-4 mr-1" /> Visit Website</Button></a>}
          </div>

          {resource.description && <p className="text-gray-700 leading-relaxed mb-4">{resource.description}</p>}

          <div className="divide-y divide-gray-100">
            {resource.phone && <Row icon={Phone} label="Phone"><a href={`tel:${resource.phone.replace(/[^0-9]/g, '')}`} className="text-primary hover:underline">{resource.phone}</a></Row>}
            {resource.website && <Row icon={Globe} label="Website"><a href={resource.website} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{resource.website}</a></Row>}
            {resource.email && <Row icon={Mail} label="Email"><a href={`mailto:${resource.email}`} className="text-primary hover:underline">{resource.email}</a></Row>}
            {(resource.address || loc || resource.zip_code) && <Row icon={MapPin} label="Location">{[resource.address, loc, resource.zip_code].filter(Boolean).join(', ')}</Row>}
            {resource.hours && <Row icon={Clock} label="Hours">{resource.hours}</Row>}
            {resource.eligibility && <Row icon={Users} label="Who It Helps / Eligibility">{resource.eligibility}</Row>}
            {resource.languages && <Row icon={Users} label="Languages">{resource.languages}</Row>}
            {resource.is_national && <Row icon={CheckCircle2} label="Service Area">Available nationwide</Row>}
          </div>

          <div className="mt-5 pt-4 border-t flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-gray-400">
              {resource.last_verified ? `Last verified ${String(resource.last_verified).slice(0, 10)}` : 'Information subject to change.'}
              {' '}Work here or know this organization?
            </p>
            <ClaimResourceModal resource={resource} triggerClassName="bg-secondary hover:bg-secondary-hover text-white" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceDetail;
